/**
 * `ColorField` (W1-12 ui-kit-i18n; contract ColorFieldProps): an RGB colour (integer channels 0..255, RPE
 * `[r, g, b]`). The field shows a swatch and the hex code (or "None (default)" / "Mixed") and opens a picker
 * popover with:
 *  - "None (default)" when `allowNone` (note tint, hit-effect colour),
 *  - a row of common colours,
 *  - Red / Green / Blue whole-number fields and a hex field (#RRGGBB or #RGB), committed on Enter / blur, with
 *    an error under the field for invalid input,
 *  - the system colour picker ("More colours"), committed when the system dialog closes.
 * Every commit is one `onCommit` call; values equal to the current colour are not committed again.
 */

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { RGB } from '../core/util/types';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n/types';
import s from './ColorField.module.css';
import { Icon } from './Icon';
import { parseChannel, parseHex, rgbToHex, sameRgb } from './internal/color';
import { cx } from './internal/cx';
import { pendingEditProps } from './internal/layers';
import f from './internal/form.module.css';
import { keepFocusOnPointerDown } from './internal/focus';
import { Popover } from './Popover';
import { Swatch } from './Swatch';
import type { ColorFieldProps } from './types';
import { m } from './ui.messages';

/** Quick colours offered in the picker (chart data colours, not UI tokens): white, greys, the note colours, hues. */
export const COLOR_PRESETS: readonly RGB[] = [
  [255, 255, 255],
  [190, 190, 190],
  [96, 96, 96],
  [0, 0, 0],
  [10, 195, 255],
  [240, 237, 105],
  [254, 67, 101],
  [255, 236, 160],
  [255, 120, 60],
  [80, 220, 120],
  [90, 130, 255],
  [190, 110, 255],
];

type Current = { readonly kind: 'none' } | { readonly kind: 'mixed' } | { readonly kind: 'rgb'; readonly rgb: RGB };

function currentOf(value: ColorFieldProps['value']): Current {
  if (value === null) return { kind: 'none' };
  return value.kind === 'mixed' ? { kind: 'mixed' } : { kind: 'rgb', rgb: value.value };
}

/** A small text input committed on Enter / blur, restored on Escape, validated by `parse`. */
function CommitInput<T>(props: {
  readonly label: string;
  readonly value: string;
  readonly placeholder?: string | undefined;
  readonly parse: (s: string) => T | null;
  readonly invalid: MessageKey;
  onCommit(v: T): void;
  readonly className?: string | undefined;
  readonly inputMode?: 'numeric' | 'text';
  readonly maxLength?: number;
  readonly disabled?: boolean;
}) {
  const t = useT();
  const id = useId();
  const errorId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const commit = (keepOnError: boolean): void => {
    if (draft === null) return;
    const parsed = props.parse(draft);
    if (parsed === null) {
      if (keepOnError) setError(true);
      else {
        setDraft(null);
        setError(false);
      }
      return;
    }
    setDraft(null);
    setError(false);
    props.onCommit(parsed);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(true);
    } else if (e.key === 'Escape' && draft !== null) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
      setError(false);
    }
  };
  return (
    <div className={cx(s.channel, props.className)}>
      <label htmlFor={id} className={f.label}>
        {props.label}
      </label>
      <input
        id={id}
        className={cx(f.input, s.channelInput)}
        type="text"
        inputMode={props.inputMode ?? 'text'}
        autoComplete="off"
        spellCheck={false}
        maxLength={props.maxLength}
        disabled={props.disabled}
        value={draft ?? props.value}
        placeholder={props.placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          setError(false);
        }}
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        onBlur={() => {
          commit(false);
        }}
        {...pendingEditProps(draft !== null)}
        onKeyDown={onKeyDown}
      />
      {error && (
        <p id={errorId} className={f.error}>
          {t(props.invalid)}
        </p>
      )}
    </div>
  );
}

export function ColorField(props: ColorFieldProps & { readonly className?: string | undefined }) {
  const t = useT();
  const labelId = useId();
  const valueId = useId();
  const current = currentOf(props.value);
  const rgb = current.kind === 'rgb' ? current.rgb : null;

  const commit = (next: RGB | null): void => {
    if (next === null) {
      if (current.kind !== 'none') props.onCommit(null);
      return;
    }
    if (rgb !== null && sameRgb(rgb, next)) return;
    props.onCommit(next);
  };
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  });

  // The system picker reports intermediate colours on 'input'; only its final 'change' is committed. The input
  // lives in the popover (mounted on open), so the listener is attached by a callback ref.
  const attachSystemPicker = useCallback((el: HTMLInputElement | null) => {
    if (el === null) return undefined;
    const onChange = (): void => {
      const parsed = parseHex(el.value);
      if (parsed !== null) commitRef.current(parsed);
    };
    el.addEventListener('change', onChange);
    return () => {
      el.removeEventListener('change', onChange);
    };
  }, []);

  const valueText =
    current.kind === 'none' ? t(m['color.noneValue']) : current.kind === 'mixed' ? t(m.mixed) : rgbToHex(current.rgb);
  const channelNames: readonly [MessageKey, MessageKey, MessageKey] = [
    m['color.red'],
    m['color.green'],
    m['color.blue'],
  ];

  const trigger = (
    <button
      type="button"
      className={s.trigger}
      aria-labelledby={`${labelId} ${valueId}`}
      onMouseDown={keepFocusOnPointerDown}
    >
      {current.kind === 'rgb' ? (
        <Swatch color={current.rgb} size={16} />
      ) : (
        <span className={cx(s.placeholderSwatch, current.kind === 'none' ? s.none : s.mixed)} aria-hidden />
      )}
      <span id={valueId} className={cx(s.valueText, current.kind !== 'rgb' && s.muted)}>
        {valueText}
      </span>
      <Icon name="ChevronDown" size={14} />
    </button>
  );

  return (
    <div className={cx(s.field, props.className)} data-help-id={props.helpId}>
      <span id={labelId} className={cx(f.label, s.label)}>
        <span className={f.labelText}>{props.label}</span>
      </span>
      <Popover trigger={trigger} side="bottom" align="start" label={t(m['color.pick'])} contentClassName={s.popover}>
        <div className={s.picker}>
          {props.allowNone === true && (
            <button
              type="button"
              className={cx(s.noneButton, current.kind === 'none' && s.noneActive)}
              aria-pressed={current.kind === 'none'}
              onClick={() => {
                commit(null);
              }}
            >
              <span className={cx(s.placeholderSwatch, s.none)} aria-hidden />
              {t(m['color.none'])}
            </button>
          )}
          <div className={s.presets} role="group" aria-label={t(m['color.presets'])}>
            {COLOR_PRESETS.map((c) => {
              const hex = rgbToHex(c);
              const active = rgb !== null && sameRgb(rgb, c);
              return (
                <button
                  key={hex}
                  type="button"
                  className={cx(s.preset, active && s.presetActive)}
                  aria-label={t(m['color.value'], { hex })}
                  aria-pressed={active}
                  onClick={() => {
                    commit(c);
                  }}
                >
                  <Swatch color={c} size={18} />
                </button>
              );
            })}
          </div>
          <div className={s.channels}>
            {channelNames.map((name, i) => (
              <CommitInput<number>
                key={name}
                label={t(name)}
                inputMode="numeric"
                maxLength={3}
                disabled={current.kind === 'mixed'}
                value={rgb === null ? '' : String(rgb[i] ?? 0)}
                placeholder={current.kind === 'mixed' ? t(m.mixed) : undefined}
                parse={parseChannel}
                invalid={m['color.channelRange']}
                onCommit={(v) => {
                  const base: RGB = rgb ?? [255, 255, 255];
                  const next: [number, number, number] = [base[0], base[1], base[2]];
                  next[i] = v;
                  commit(next);
                }}
              />
            ))}
          </div>
          <div className={s.hexRow}>
            <CommitInput<RGB>
              label={t(m['color.hex'])}
              className={s.hex}
              maxLength={7}
              value={rgb === null ? '' : rgbToHex(rgb)}
              placeholder={current.kind === 'mixed' ? t(m.mixed) : t(m['color.hexPlaceholder'])}
              parse={parseHex}
              invalid={m['color.invalidHex']}
              onCommit={commit}
            />
            <label className={s.system}>
              <span className={f.label}>{t(m['color.system'])}</span>
              <span className={s.systemButton}>
                <Icon name="Pipette" size={14} />
                <input
                  key={rgb === null ? 'none' : rgbToHex(rgb)}
                  ref={attachSystemPicker}
                  type="color"
                  className={s.nativeInput}
                  defaultValue={rgb === null ? '#ffffff' : rgbToHex(rgb).toLowerCase()}
                  aria-label={t(m['color.system'])}
                />
              </span>
            </label>
          </div>
        </div>
      </Popover>
    </div>
  );
}
